// services/assistant.service.js
import assistantClient, { handleApiError } from '../api/assistantClient';

/**
 * Servicio para gestionar Assistants
 */
class AssistantService {
          /**
           * Obtener lista de skills disponibles para asistentes
           * Documentación API: GET /assistants/skills
           * @returns {Object} response { success, data: { skills: [...], total } }
           */
          async getSkills() {
            try {
              const response = await assistantClient.get('/assistants/skills');
              return { success: true, data: response.data };
            } catch (error) {
              return { success: false, error: handleApiError(error) };
            }
          }
        /**
         * Listar historiales de chat de un usuario
         * Documentación API: GET /chat/histories?user_id=...
         * @param {Object} params - { userId, limit, offset, sortBy, descending }
         * @returns {Object} response
         */
        async listChatHistories({ user_id, limit = 10, offset = 0, sort_by = 'updated_at', descending = true } = {}) {
          try {
            const response = await assistantClient.get('/chat/histories', {
              params: {
                user_id,
                limit,
                offset,
                sort_by,
                descending,
              },
            });
            return { success: true, data: response.data };
          } catch (error) {
            return { success: false, error: handleApiError(error) };
          }
        }

        /**
         * Listar historiales de chat para un assistant específico
         * Documentación API: GET /accounts/{account_id}/chat/histories?assistant_id={assistant_id}
         * @param {Object} params - { accountId, assistantId, limit, offset, sortBy, descending }
         * @returns {Object} response
         */
        async listChatHistoriesByAssistant({ accountId, assistantId, limit = 10, offset = 0, sort_by = 'updated_at', descending = true } = {}) {
          try {
            if (!accountId || !assistantId) {
              return { 
                success: false, 
                error: { message: 'accountId and assistantId are required' }
              };
            }

            const response = await assistantClient.get(`/accounts/${accountId}/chat/histories`, {
              params: {
                assistant_id: assistantId,
                limit,
                offset,
                sort_by,
                descending,
              },
            });
            
            return { 
              success: true, 
              data: response.data.chatHistories || response.data || [],
              total: response.data.total || 0,
              limit: response.data.limit || limit,
              offset: response.data.offset || offset
            };
          } catch (error) {
            return { success: false, error: handleApiError(error) };
          }
        }
    /**
     * Crear un nuevo historial de chat
     * Documentación API: POST /chat/history
     * @param {Object} params - { userId, userTimezone, assistantId, title, externalId, contextVariables, systemPrompt }
     * @returns {Object} response
     */
    async createChatHistory(params) {
      try {
        const response = await assistantClient.post('/chat/histories', params);
        return { success: true, data: response.data };
      } catch (error) {
        return { success: false, error: handleApiError(error) };
      }
    }
  /**
   * Helper para limpiar/sanear datos de assistant de la API
   * Filtra campos no permitidos para evitar errores de validación
   */
  sanitizeAssistantData(assistant) {
    if (!assistant) return assistant;
    
    // Lista de campos no permitidos en el config según la documentación
    // Nota: structuredOutputEnabled es válido según la documentación, pero agregamos
    // otros campos problemáticos que pueden venir de la API
    const forbiddenConfigFields = [
      // Campos que puedan venir con nombres incorrectos o deprecated
      'structured_output_enabled', // Deprecated, debe ser structuredOutputEnabled
      'memory_enabled', // Deprecated, debe ser memoryEnabled
      'is_active', // Deprecated, debe ser isActive
      'is_draft', // Deprecated, debe ser isDraft
      'llm_id', // Deprecated, debe ser llmId
      'model_label', // Deprecated, debe ser modelLabel
      'assistant_template', // Deprecated, debe ser assistantTemplate
      'template_id', // Deprecated, debe ser templateId
      'max_tokens', // Deprecated, debe ser maxTokens
      'pretty_print', // Deprecated, debe ser prettyPrint
      'root_prompt', // Deprecated, debe ser rootPrompt
      'reasoning_effort', // Deprecated, debe ser reasoningEffort
      'custom_parameters', // Deprecated, debe ser customParameters
    ];
    
    // Si hay config, limpiar campos no permitidos
    if (assistant.config && typeof assistant.config === 'object') {
      const cleanConfig = { ...assistant.config };
      
      // Remover campos no permitidos
      forbiddenConfigFields.forEach(field => {
        delete cleanConfig[field];
      });
      
      assistant = {
        ...assistant,
        config: cleanConfig
      };
    }
    
    return assistant;
  }

  /**
   * Helper para crear configuración por defecto según documentación
   * @param {string} accountId - ID de la cuenta (required para assistant_template)
   * @param {string} projectId - ID del proyecto (optional)
   */
  getDefaultConfig(accountId, projectId = null) {
    return {
      // Required fields según documentación
      llmId: "b7cad7e6-7369-4a1a-a3e1-1923615d6ccc",
      assistantTemplate: {
        id: "00000000-0000-0000-0000-000000000001",
        name: "General Assistant",
        description: "A versatile general-purpose assistant template",
        account_id: accountId, // Required field según error
        project_id: projectId, // Optional
        is_default: true,
        is_public: true, // Visible across account
        sections: [
          {
            id: "00000000-0000-0000-0000-000000000011",
            template_id: "00000000-0000-0000-0000-000000000001",
            name: "Identity",
            order: 1,
            content: "You are a helpful, knowledgeable, and professional AI assistant."
          },
          {
            id: "00000000-0000-0000-0000-000000000012",
            template_id: "00000000-0000-0000-0000-000000000001",
            name: "Behavior",
            order: 2,
            content: "Be concise yet thorough in your responses. Always maintain a friendly and professional tone."
          }
        ]
      },
      
      // Optional fields con valores por defecto
      modelLabel: "GPT-4o Mini", 
      templateId: "00000000-0000-0000-0000-000000000001",
      skills: [],
      temperature: 0.7,
      maxTokens: 2048,
      rootPrompt: null,
      reasoningEffort: null,
      memoryEnabled: true,
      structuredOutputEnabled: false,
      prettyPrint: false,
      language: "en",
      isActive: true,
      isDraft: true,
      customParameters: {}
    };
  }

  /**
   * Helper para crear un assistant con configuración por defecto
   */
  createDefaultAssistant({ name, description, userId, accountId, projectId = null, type = "general" }) {
    return {
      name: name,
      description: description || "A sample assistant for testing purposes",
      type: type, // "workflow" | "config" | "general" | "custom"
      skills: [],
      memory: "",
      executionMode: "reactive",
      config: this.getDefaultConfig(accountId, projectId), // Pasar accountId y projectId
      createdByUserId: userId,
      accountId: accountId,
      projectId: projectId
    };
  }
  /**
   * Normaliza el config para asegurar que tenga la estructura correcta
   * Convierte nombres de snake_case a camelCase y asegura campos requeridos
   */
  normalizeConfig(config, accountId, projectId = null) {
    // Si no hay config o está vacío, usar el default
    if (!config || Object.keys(config).length === 0) {
      return this.getDefaultConfig(accountId, projectId);
    }

    // Mapeo de campos snake_case a camelCase
    const fieldMapping = {
      llm_id: 'llmId',
      model_label: 'modelLabel',
      assistant_template: 'assistantTemplate',
      template_id: 'templateId',
      max_tokens: 'maxTokens',
      memory_enabled: 'memoryEnabled',
      is_active: 'isActive',
      is_draft: 'isDraft',
      custom_parameters: 'customParameters',
      pretty_print: 'prettyPrint',
      root_prompt: 'rootPrompt',
      reasoning_effort: 'reasoningEffort',
      structured_output_enabled: 'structuredOutputEnabled'
    };

    // Normalizar campos del config
    const normalizedConfig = {};
    
    // Copiar campos con nombres correctos
    Object.keys(config).forEach(key => {
      const normalizedKey = fieldMapping[key] || key;
      normalizedConfig[normalizedKey] = config[key];
    });

    // Asegurar campos requeridos
    normalizedConfig.llmId = normalizedConfig.llmId || "b7cad7e6-7369-4a1a-a3e1-1923615d6ccc";
    
    // Normalizar assistantTemplate
    if (normalizedConfig.assistantTemplate) {
      normalizedConfig.assistantTemplate = {
        ...normalizedConfig.assistantTemplate,
        account_id: accountId,
        project_id: projectId
      };
    } else {
      // Si no hay assistantTemplate, usar el default
      normalizedConfig.assistantTemplate = this.getDefaultConfig(accountId, projectId).assistantTemplate;
    }

    return normalizedConfig;
  }

  /**
   * Crear un nuevo assistant
   * Documentación API: POST /assistants
   * Actualizado según documentación oficial con campos correctos
   */
  async createAssistant(assistantData) {
    try {
      // Validar que los campos requeridos estén presentes
      const requiredFields = ['name', 'type', 'config', 'createdByUserId', 'accountId'];
      const missingFields = requiredFields.filter(field => !assistantData[field]);
      
      if (missingFields.length > 0) {
        return { 
          success: false, 
          error: `Missing required fields: ${missingFields.join(', ')}` 
        };
      }

      // Normalizar config para asegurar estructura correcta y account_id en assistantTemplate
      const normalizedConfig = this.normalizeConfig(
        assistantData.config, 
        assistantData.accountId, 
        assistantData.projectId
      );

      // Estructura del body según documentación oficial
      const requestBody = {
        // Campos opcionales primero
        ...(assistantData.id && { id: assistantData.id }), // UUID opcional
        
        // Campos requeridos
        name: assistantData.name,
        type: assistantData.type, // Required: "workflow" | "config" | "general" | "custom"
        config: normalizedConfig, // Required object con estructura normalizada
        createdByUserId: assistantData.createdByUserId, // Required UUID
        accountId: assistantData.accountId, // Required UUID
        
        // Campos opcionales con defaults
        description: assistantData.description || null,
        skills: assistantData.skills || [],
        avatar: assistantData.avatar || null,
        reactAgentId: assistantData.reactAgentId || null,
        memory: assistantData.memory || "",
        executionMode: assistantData.executionMode || "reactive", // Default según docs
        projectId: assistantData.projectId || null, // Optional UUID
      };

      console.log('🚀 Sending normalized assistant creation request:', JSON.stringify(requestBody, null, 2));

      const response = await assistantClient.post('/assistants', requestBody);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Assistant creation failed:', error.response?.data || error.message);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar assistants del usuario/cuenta
   * Documentación API: GET /assistants
   * Parámetros query: owner_id, user_id (deprecated), limit, offset, sort_by, sort_order
   * Respuesta: { success: true, data: [...], total: number, limit: number, offset: number }
   */
  async listAssistants({ 
    ownerId, 
    userId, // Deprecated but included for compatibility 
    limit = 10, 
    offset = 0, 
    sortBy = 'created_at', 
    sortOrder = 'desc',
    projectId // For project filtering (frontend only)
  } = {}) {
    try {
      const params = {};
      
      // Owner ID tiene prioridad sobre user_id (deprecated)
      if (ownerId) params.owner_id = ownerId;
      else if (userId) params.user_id = userId; // Deprecated, use owner_id
      
      // Validar y establecer límites según documentación
      if (limit !== undefined) params.limit = Math.min(Math.max(limit, 1), 100); // Min 1, Max 100
      if (offset !== undefined && offset >= 0) params.offset = offset; // Min 0
      if (sortBy) params.sort_by = sortBy;
      if (sortOrder) params.sort_order = sortOrder;
      
      const response = await assistantClient.get('/assistants', { params });
      
      // La respuesta oficial viene con estructura: { success: true, data: [...], total, limit, offset }
      let assistants = response.data.data || [];
      
      // Sanear datos de la API para evitar errores de validación
      assistants = assistants.map(assistant => this.sanitizeAssistantData(assistant));
      
      // Filtrar por proyecto si se proporciona projectId (filtrado frontend)
      // Cada assistant tiene el campo projectId según la documentación
      if (projectId) {
        assistants = assistants.filter(assistant => assistant.projectId === projectId);
        
        // Actualizar el total si hay filtrado local
        const filteredTotal = assistants.length;
        return {
          success: true,
          data: assistants,
          total: filteredTotal,
          limit: response.data.limit || limit,
          offset: response.data.offset || offset,
        };
      }
      
      return { 
        success: true, 
        data: assistants,
        total: response.data.total || 0,
        limit: response.data.limit || limit,
        offset: response.data.offset || offset,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener assistant por ID
   * Documentación API: GET /assistants/{id}
   * Respuesta: Assistant object con todos los campos documentados
   */
  async getAssistant(assistantId) {
    try {
      // Validar que se proporcione el ID
      if (!assistantId) {
        return { 
          success: false, 
          error: { message: 'Assistant ID is required' }
        };
      }

      const response = await assistantClient.get(`/assistants/${assistantId}`);
      
      // La respuesta debe contener el objeto assistant completo
      // Sanear datos antes de retornar
      const sanitizedData = this.sanitizeAssistantData(response.data);
      
      return { 
        success: true, 
        data: sanitizedData 
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar assistant
   */
  async updateAssistant(assistantId, updates) {
    try {
      // Eliminar todos los console.log y mensajes de depuración
      
      const payloadSize = JSON.stringify(updates).length;
      
      // Validar tamaño del payload (límite 1MB)
      const maxPayloadSize = 1024 * 1024; // 1MB
      if (payloadSize > maxPayloadSize) {
        console.warn('⚠️ Payload demasiado grande:', payloadSize, 'bytes');
        
        // Si el avatar es muy grande, eliminarlo temporalmente
        if (updates.avatar && updates.avatar.length > 500000) { // 500KB
          console.log('🗜️ Avatar demasiado grande, removiendo temporalmente...');
          const updatesWithoutAvatar = { ...updates };
          delete updatesWithoutAvatar.avatar;
          console.log('📦 Nuevo tamaño sin avatar:', JSON.stringify(updatesWithoutAvatar).length, 'bytes');
          
          const response = await assistantClient.put(`/assistants/${assistantId}`, updatesWithoutAvatar);
          return { success: true, data: response.data };
        }
      }
      
      const response = await assistantClient.put(`/assistants/${assistantId}`, updates);
      
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar assistant
   */
  async deleteAssistant(assistantId, deletionData) {
    try {
      const response = await assistantClient.delete(`/assistants/${assistantId}`, {
        data: deletionData,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar assistants por cuenta
   */
  async listAccountAssistants(accountId, limit = 20) {
    try {
      const response = await assistantClient.get(`/accounts/${accountId}/assistants`, {
        params: { limit },
      });
      return { 
        success: true, 
        data: response.data.assistants || [],
        total: response.data.total || 0,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener estadísticas de uso del assistant
   */
  async getAssistantStats(assistantId) {
    try {
      const response = await assistantClient.get(`/assistants/${assistantId}/usage-stats`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar templates disponibles
   */
  async listTemplates() {
    try {
      const response = await assistantClient.get('/assistant_templates');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar modelos LLM disponibles
   * GET /llms
   */
  async listLLMs(enabledOnly = true) {
    try {
      const params = { enabled_only: enabledOnly };
      const response = await assistantClient.get('/llms', { params });
      return { 
        success: true, 
        data: response.data.llms,
        total: response.data.total
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }
    /**
     * Enviar mensaje a un assistant y recibir respuesta
     * Documentación API: POST /assistants/message
     * @param {Object} params - { assistant_id, user_id, message, chat_history_id, context }
     * @returns {Object} response
     */
    async sendMessageToAssistant(params) {
      try {
        const response = await assistantClient.post('/chat/assistant', params);
        return { success: true, data: response.data };
      } catch (error) {
        return { success: false, error: handleApiError(error) };
      }
    }
}

export default new AssistantService();
